"use client";
import { KnootLogoIcon } from "@/_images/svgs/chakra-icons/Logo";
import {
  Container,
  Flex,
  HStack,
  Icon,
  Text,
  IconButton,
  Drawer,
  Stack,
  Box,
} from "@chakra-ui/react";
import { usePathname } from "next/navigation";
import { SearchBox } from "./SearchBox";
import { NextLink } from "@/_components/ui/NextLink";
import { RxCross2, RxHamburgerMenu } from "react-icons/rx";
import { CustomLine } from "@/_components/ui/CustomLine";
import { useTranslations } from "next-intl";
import {
  HiBriefcase,
  HiMiniHome,
  HiMiniPlay,
  HiMiniStar,
} from "react-icons/hi2";

export const BlogHeader = () => {
  const pathName = usePathname();
  const t = useTranslations("landing.blog.header");
  const listItems = [
    { name: t("home"), href: "/blog", id: "home", icon: HiMiniHome },
    { name: t("career"), href: "/blog/career", id: "career", icon: HiMiniStar },
    {
      name: t("skill_station"),
      href: "/blog/skill-station",
      id: "skill-station",
      icon: HiBriefcase,
    },
    {
      name: t("podcasts"),
      href: "/blog/podcasts",
      id: "podcasts",
      icon: HiMiniPlay,
    },
  ];

  const isItemActive = (item: (typeof listItems)[0]) => {
    if (item.id === "home") {
      return pathName.endsWith("/blog") && !pathName.includes("/blog/");
    }
    return pathName.includes(item.id);
  };

  const renderLogo = () => {
    return (
      <NextLink href="/blog" borderBottom="none" outline="none">
        <HStack cursor={"pointer"} alignItems="center" gap={2}>
          <KnootLogoIcon boxSize={{ base: 8, md: 10 }} />
          <Text
            textStyle={{ base: "xl", md: "xl" }}
            fontFamily="heading"
            fontWeight="light "
          >
            {t("logo")}
          </Text>
        </HStack>
      </NextLink>
    );
  };

  const renderNavigationLinks = () => {
    return listItems.map((item, index) => (
      <NextLink
        href={item.href}
        key={item.id}
        outline="none"
        borderBottom="none"
        rounded="full"
        px={{ base: isItemActive(item) ? 4 : 0, md: 4 }}
        py={2}
        bg={
          isItemActive(item)
            ? { base: "primary.50", md: "primary.600" }
            : "transparent"
        }
        color={
          isItemActive(item)
            ? { base: "primary.600", md: "white" }
            : "neutral.600"
        }
        _hover={{
          bg: isItemActive(item) ? "primary.600" : "neutral.100",
          shadow: "shadow",
        }}
      >
        <HStack alignItems="center" justifyContent="center" gap={2}>
          <Icon
            as={item.icon}
            boxSize={{ base: 7, md: 5 }}
            color={
              isItemActive(item)
                ? { base: "primary.600", md: "white" }
                : "inherit"
            }
          />
          <Text
            key={index}
            textStyle={{ base: "2xl", md: "sm" }}
            fontFamily={{ base: "heading", md: "body" }}
            color={
              isItemActive(item)
                ? { base: "primary.600", md: "white" }
                : "inherit"
            }
            fontWeight="light"
            cursor="pointer"
          >
            {item.name}
          </Text>
        </HStack>
      </NextLink>
    ));
  };

  const renderResponsiveMenu = (trigger: React.ReactNode) => {
    return (
      <Drawer.Root size={{ base: "full", md: "sm" }}>
        <Drawer.Backdrop />
        <Drawer.Trigger>{trigger}</Drawer.Trigger>
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.CloseTrigger asChild mt={2} mr={2}>
              <Icon as={RxCross2} boxSize={6} color="neutral.700" />
            </Drawer.CloseTrigger>
            <Drawer.Header>{renderLogo()}</Drawer.Header>
            <CustomLine variant="dashed" color="primary.600" mb={4} />
            <Drawer.Body>
              <Stack gap={8} alignItems="flex-start">
                {renderNavigationLinks()}
              </Stack>
            </Drawer.Body>
            <Drawer.Footer>
              <Box w="full">
                <SearchBox />
              </Box>
            </Drawer.Footer>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>
    );
  };

  return (
    <Flex>
      <Container maxW="full">
        <Flex justify="space-between" py={4} alignItems="center">
          <HStack gap={12}>
            {renderLogo()}
            <HStack
              gap={2}
              hideBelow="lg"
              p={1}
              rounded="full"
              bg="neutral.100"
            >
              {renderNavigationLinks()}
            </HStack>
          </HStack>
          <HStack gap={4} alignItems="center">
            <Box hideFrom="lg">
              {renderResponsiveMenu(
                <IconButton
                  aria-label={t("menu")}
                  as={RxHamburgerMenu}
                  visual="none-style"
                  colorPalette="neutral"
                  size="sm"
                  p={0.5}
                  _hover={{
                    bg: "neutral.100/20",
                  }}
                />,
              )}
            </Box>
            <Box hideBelow="lg">
              <SearchBox />
            </Box>
          </HStack>
        </Flex>
      </Container>
    </Flex>
  );
};
